import { motion } from 'framer-motion';
import {
  Lightbulb,
  Target,
  Zap,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Shield,
  Crown,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';
import Breadcrumb from '../components/Breadcrumb';
import PageHero from '../components/PageHero';
import { getPageHeroImageUrl } from '../config/cloudflareAssets';

export default function StrategyFormulationPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const strategicApproaches = [
    {
      icon: Crown,
      title: 'Competitive Strategy',
      subtitle: 'How Will You Win?',
      description: 'Choose your fundamental approach to competing in your market.',
      link: '/strategy-formulation/competitive-strategy',
      options: [
        {
          name: 'Cost Leadership',
          description: 'Become the low-cost provider in your industry',
          when: 'Best when: You can achieve economies of scale and operational efficiency',
          examples: ['Walmart', 'Southwest Airlines', 'IKEA'],
        },
        {
          name: 'Differentiation',
          description: 'Offer unique value that customers will pay premium prices for',
          when: 'Best when: You have distinctive capabilities or serve specific customer needs',
          examples: ['Apple', 'Tesla', 'Starbucks'],
        },
        {
          name: 'Focus Strategy',
          description: 'Serve a specific market segment better than anyone else',
          when: 'Best when: You can deeply understand and serve a niche market',
          examples: ['Ferrari', 'Whole Foods', 'Peloton'],
        },
      ],
    },
    {
      icon: TrendingUp,
      title: 'Growth Strategy',
      subtitle: 'How Will You Grow?',
      description: 'Select the path to expand your business and achieve your vision.',
      link: '/strategy-formulation/growth-strategy',
      options: [
        {
          name: 'Market Penetration',
          description: 'Sell more to existing customers in existing markets',
          when: "Best when: Your current market isn't saturated and you have room to grow share",
          examples: [
            'Increase sales to current customers',
            "Win competitors' customers",
            'Increase usage frequency',
          ],
        },
        {
          name: 'Market Development',
          description: 'Take existing products to new markets or customer segments',
          when: 'Best when: Your product can serve new geographies or demographics',
          examples: ['Geographic expansion', 'New customer segments', 'New distribution channels'],
        },
        {
          name: 'Product Development',
          description: 'Create new products or services for existing customers',
          when: 'Best when: You understand customer needs and have innovation capabilities',
          examples: ['New product lines', 'Feature enhancements', 'Complementary services'],
        },
        {
          name: 'Diversification',
          description: 'New products in new markets (highest risk, highest potential)',
          when: 'Best when: Core business is mature and you have resources to invest in new ventures',
          examples: [
            'Amazon Web Services (from retail)',
            'Apple Watch (from computers)',
            'Disney+ (from movies)',
          ],
        },
      ],
    },
    {
      icon: Shield,
      title: 'Strategic Initiatives',
      subtitle: 'What Will You Do?',
      description:
        'Define specific initiatives that will move you from where you are to where you want to be.',
      link: '/strategy-formulation/strategic-initiatives',
      options: [
        {
          name: 'Initiative Selection Criteria',
          description: 'How to choose which initiatives to pursue',
          when: 'Evaluate based on: Strategic alignment, resource requirements, expected impact, timeline',
          examples: [
            'Launch new product line',
            'Build strategic partnership',
            'Enter new market',
            'Digital transformation',
          ],
        },
        {
          name: 'Prioritization',
          description: "You can't do everything at once - rank initiatives by importance",
          when: 'Consider: Impact on objectives, resource constraints, dependencies, quick wins vs. long-term bets',
          examples: [
            'Must-have initiatives',
            'Should-have initiatives',
            'Nice-to-have initiatives',
          ],
        },
        {
          name: 'Resource Allocation',
          description: 'Decide how to deploy limited resources across initiatives',
          when: 'Balance: Strategic importance, expected ROI, risk level, capability requirements',
          examples: [
            'Budget allocation',
            'Team assignments',
            'Technology investments',
            'Time commitment',
          ],
        },
      ],
    },
  ];

  const benefits = [
    {
      icon: Target,
      title: 'Make the direction explicit',
      description:
        'Choose a strategic approach and explain how it supports the objectives you have set.',
    },
    {
      icon: CheckCircle,
      title: 'Show the reasoning',
      description:
        'Use your external and internal assessments to compare options and document the trade-offs.',
    },
    {
      icon: Zap,
      title: 'Put capacity behind priorities',
      description:
        'Decide which initiatives deserve your people, time and budget before work begins.',
    },
    {
      icon: Shield,
      title: 'Plan what to strengthen',
      description: 'Identify the capabilities and relationships your chosen approach depends on.',
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'Strategy Formulation' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Lightbulb className="w-4 h-4 mr-2" />
            Step 4 of 5
          </span>
        }
        title="Choose the priorities that will move you forward."
        subtitle="Bring your direction, market insight and company strengths together. Compare strategic options, make the trade-offs clear and choose the initiatives worth pursuing."
      >
        <Link
          to="/sign-up"
          className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90 transition-opacity"
        >
          Choose my strategic priorities
        </Link>
      </PageHero>

      {/* Strategic Approaches */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">Make the choices behind your next chapter.</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Select strategies that match your strengths, address your market realities, and
              achieve your objectives
            </p>
          </motion.div>

          <div className="space-y-16 max-w-6xl mx-auto">
            {strategicApproaches.map((approach, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card bg-gradient-to-br from-indigo-50 to-white rounded-2xl p-8 border border-indigo-200"
              >
                <div className="flex flex-col md:flex-row gap-8 mb-8">
                  <div className="flex-shrink-0">
                    <div className="bg-gradient-to-br from-indigo-600 to-purple-600 w-20 h-20 rounded-xl flex items-center justify-center shadow-sm">
                      <approach.icon className="w-10 h-10 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold mb-2">{approach.title}</h3>
                    <p className="text-indigo-600 font-semibold mb-4 text-lg">
                      {approach.subtitle}
                    </p>
                    <p className="text-gray-700 text-lg">{approach.description}</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-4">
                  {approach.options.map((option, idx) => (
                    <div
                      key={idx}
                      className="public-card bg-white p-6 rounded-xl border border-indigo-200"
                    >
                      <h4 className="font-bold text-gray-900 mb-2">{option.name}</h4>
                      <p className="text-gray-700 mb-3 text-sm font-medium">{option.description}</p>
                      <p className="text-xs text-indigo-600 mb-3 italic">{option.when}</p>
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-700">Examples:</p>
                        {typeof option.examples === 'string' ? (
                          <p className="text-xs text-gray-600">{option.examples}</p>
                        ) : (
                          option.examples.map((example, i) => (
                            <div key={i} className="flex items-start">
                              <div className="bg-indigo-400 w-1 h-1 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                              <span className="text-xs text-gray-600">{example}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Learn More Link */}
                <Link
                  to={approach.link}
                  className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-semibold transition-colors"
                >
                  Learn More About {approach.title} <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">
                A clear strategy makes it easier to say yes and no.
              </h2>
              <p className="text-xl text-gray-600">
                This is where analysis turns into action. Without clear strategy, you're just
                reacting to circumstances.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="public-card text-center bg-white p-6 rounded-xl shadow-sm"
                >
                  <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section public-cta py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-4">
              Give your priorities an owner and a next step.
            </h2>
            <p className="text-gray-600 text-center mb-8">
              Connect the initiatives you choose to projects, tasks and a review rhythm your team
              can follow.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/internal-assessment"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                ← Previous: Internal Assessment
              </Link>
              <Link to="/execution-monitoring" className="public-button public-button--primary">
                Next: Execution & Monitoring <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
